import Route from '@ember/routing/route';
import { service } from '@ember/service';
// This is for types. Anything we have custom may show an error.
import type { Store as StoreService } from '@warp-drive/core';
import type SessionContextService from 'tio-employee/services/session-context';
import { OpenSearchQueryBuilder } from 'tio-common/utils/open-search-builder';
import { OpenSearchResponseBuilder } from 'tio-common/utils/open-search-response-builder';
import { executeSearchRequest } from 'tio-data/requests/search';
import type { FilterClause } from 'tio-common/utils/open-search-builder';
import type { OpenSearchResponse } from 'tio-data/data/resources/search.ext';
import type { TasProgramInstance } from 'tio-data/data/resources/search.ext';
import { normalizePaymentFields } from 'tio-common/utils/tuition-assistance/application-states';

type Params = { paymentYear: string };

const paymentStates = [
  'payment_final_approved_total_amount',
  'payment_pending_amount',
  'payment_paid_amount',
];

const stateClauses: FilterClause[][] = paymentStates.map((field) => [
  {
    bool: { must: [{ range: { [field]: { gt: 0 } } }] },
  },
]);

export default class AuthenticatedTasPaymentsRoute extends Route {
  @service declare store: StoreService;
  @service declare sessionContext: SessionContextService;

  queryParams = {
    paymentYear: {
      refreshModel: true,
      replace: true,
    },
  };

  get currentCompanyId() {
    return this.sessionContext.currentEmployee.company.id;
  }

  get currentEmployeeId() {
    return this.sessionContext.currentEmployee.id;
  }

  async model(params: Params) {
    const { paymentYear } = params;

    const builder = new OpenSearchQueryBuilder()
      .mustTerm('company_id', this.currentCompanyId)
      .mustTerm('employee_id', this.currentEmployeeId)
      .shouldEach(stateClauses)
      .setMinimumShouldMatch(1)
      .when(paymentYear, (b) => {
        const year = Number(paymentYear);
        b.range('payment_first_date', `${year}-01-01`, `${year + 1}-01-01`);
      });

    const query = {
      'model-type': 'TasProgramInstance',
      searchBody: builder.build(),
    };

    const { content: response } = (await this.store.request(executeSearchRequest(query))) as {
      content: OpenSearchResponse<TasProgramInstance>;
    };

    const responseBuilder = new OpenSearchResponseBuilder<TasProgramInstance>(response);
    const rawItems = responseBuilder.getDocuments();
    const items = normalizePaymentFields(rawItems);

    return {
      items,
      total: responseBuilder.getTotalCount(),
      took: responseBuilder.getExecutionTime(),
      isLoading: false,
    };
  }
}
